import React, { Component } from 'react';
import { IHttpApi } from '@cian/http-api';
import { RingIcon } from './icons/RingIcon';
import { Icon } from '../GroupIcons/Icon';
import { NotificationCenterModal } from '../NotificationCenterModal/NotificationCenterModal';
import { trackingNotificationIconClick } from './tracking';
import { INotificationForDesktopSchema } from '../../repositories/notification-center/entities/notification/NotificationForDesktopSchema';
import { IDesktopGetNotificationsRequest } from '../../repositories/notification-center/v1/desktop-get-notifications';
import { INotificationCenterData } from '../../../typings/window';

interface INotificationCenterProps {
  isModalOpen?: boolean;
  httpApi?: IHttpApi;
  subdomain: string;
  notificationCenterData: INotificationCenterData;
  newHeaderEnabled: boolean;
  getHasNewNotifications?(): Promise<boolean>;
  getNotifications?(parameters: IDesktopGetNotificationsRequest): Promise<INotificationForDesktopSchema[] | []>;
  markNotificationAsRead?(id: number): Promise<number | null>;
  markAllNotificationAsRead?(): Promise<void>;
  deleteAllNotifications?(): Promise<void>;
}

interface IState {
  notifications: INotificationForDesktopSchema[];
  hasUnreadNotifications: boolean;
  isModalOpen: boolean;
  isFetching: boolean;
  permission: string;
}

export class NotificationCenter extends Component<INotificationCenterProps, IState> {
  public state = {
    hasUnreadNotifications: false,
    isModalOpen: false,
    notifications: [],
    isFetching: true,
    permission: 'default',
  };

  public componentDidMount() {
    const {
      getHasNewNotifications,
      subdomain,
      notificationCenterData: { isMobileDevice },
    } = this.props;

    document.addEventListener('openNotificationCenter', () => {
      this.setState({ isModalOpen: true });
    });

    if (isMobileDevice) {
      window.addEventListener('popstate', this.closeModalOnLocationChange);
    }

    if ('Notification' in window && subdomain === 'www') {
      this.setState({ permission: Notification.permission });
    }

    if (getHasNewNotifications) {
      getHasNewNotifications()
        .then((hasUnreadNotifications: boolean) => {
          this.setState({ hasUnreadNotifications });
        })
        .catch(() => {
          this.setState({ hasUnreadNotifications: false });
        });
    }
  }

  public componentWillUnmount() {
    const {
      notificationCenterData: { isMobileDevice },
    } = this.props;
    document.removeEventListener('openNotificationCenter', () => {});

    if (isMobileDevice) {
      window.removeEventListener('popstate', this.closeModalOnLocationChange);
    }
  }

  private closeModalOnLocationChange = () => {
    if (this.state.isModalOpen && !window.location.search) {
      this.setState({ isModalOpen: false });
    }
  };

  private setQueryParam = () => {
    const queryParams = new URLSearchParams(window.location.search);
    queryParams.set('notificationModal', 'open');
    history.pushState(null, '', '?' + queryParams.toString());
  };

  private handleNotificationIconClick = () => {
    const {
      notificationCenterData: { isMobileDevice },
    } = this.props;
    trackingNotificationIconClick();
    this.setState(({ isModalOpen }) => ({ isModalOpen: !isModalOpen }));

    if (isMobileDevice) {
      this.setQueryParam();
    }
  };

  private handleClickOutsideModal = () => {
    this.setState({ isModalOpen: false });
  };

  private getUniqNotifications = (notifications: INotificationForDesktopSchema[]): INotificationForDesktopSchema[] => {
    const cache: Map<number, INotificationForDesktopSchema> = new Map();
    notifications.forEach((note: INotificationForDesktopSchema) => {
      cache.set(note.id, note);
    });

    return Array.from(cache.values());
  };

  private getNewNotifications = (lastUpdateTimestamp: number = 0, limit: number = 7) => {
    const { getNotifications } = this.props;

    if (getNotifications) {
      const parameters = { lastUpdateTimestamp, limit };
      getNotifications(parameters)
        .then((notes: INotificationForDesktopSchema[] | []) => {
          if (notes.length > 0) {
            const notifications = this.getUniqNotifications([...this.state.notifications, ...notes]);
            this.setState({ notifications });
          }
          this.setState({ isFetching: false });
        })
        .catch(() => {
          this.setState({ isFetching: false });
        });
    }
  };

  private markAsRead = (id: number) => {
    const { markNotificationAsRead } = this.props;
    if (markNotificationAsRead) {
      markNotificationAsRead(id).then(() => {
        const notifications = this.state.notifications.map((note: INotificationForDesktopSchema) =>
          note.id === id ? { ...note, isRead: true } : note,
        );

        this.setState({ notifications, hasUnreadNotifications: false });
      });
    }
  };

  private markAllAsRead = () => {
    const { markAllNotificationAsRead } = this.props;
    if (markAllNotificationAsRead) {
      markAllNotificationAsRead().then(() => {
        const notifications = this.state.notifications.map((note: INotificationForDesktopSchema) => ({
          ...note,
          isRead: true,
        }));

        this.setState({ notifications, hasUnreadNotifications: false });
      });
    }
  };

  private deleteNotifications = () => {
    const { deleteAllNotifications } = this.props;

    if (deleteAllNotifications) {
      deleteAllNotifications().then(() => {
        this.setState({ notifications: [] });
      });
    }
  };

  private toggleRedDotNotification = () => {
    this.setState({ hasUnreadNotifications: false });
  };

  public render() {
    const {
      notificationCenterData: { applicationServerKey, pushBrowserUrl, pushEnabled, isMobileDevice },
      httpApi,
      subdomain,
      newHeaderEnabled,
    } = this.props;
    const { hasUnreadNotifications, isFetching, isModalOpen, notifications, permission } = this.state;

    return (
      <>
        <Icon
          disableHref
          notifications={Number(hasUnreadNotifications)}
          onClick={this.handleNotificationIconClick}
          iconView={<RingIcon />}
          title={newHeaderEnabled ? 'Уведомления' : ''}
        />
        {isModalOpen && (
          <NotificationCenterModal
            isMobileDevice={isMobileDevice}
            permission={permission}
            subdomain={subdomain}
            httpApi={httpApi}
            applicationServerKey={applicationServerKey}
            pushBrowserUrl={pushBrowserUrl}
            pushEnabled={pushEnabled}
            isFetching={isFetching}
            notifications={notifications}
            getNewNotifications={this.getNewNotifications}
            handleClickOutsideModal={this.handleClickOutsideModal}
            markAsRead={this.markAsRead}
            markAllAsRead={this.markAllAsRead}
            deleteNotifications={this.deleteNotifications}
            toggleRedDotNotification={this.toggleRedDotNotification}
          />
        )}
      </>
    );
  }
}
